import OidcLoadingFallback from '@/components/OidcLoadingFallback';
import { baseApi } from '@/features/api/baseApi';
import {
  DIRECTUS_SSO_PROVIDER,
  initDirectusSession,
  logoutDirectusSession,
} from '@/features/api/directus';
import { loginRedirectUrl, useOidc } from '@/shared/auth/oidc';
import { useIsAdminOrUser } from '@/shared/auth/useDirectusUser';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

const CALLBACK_PATH = '/auth/directus-callback';
const debug = false;

type AuthGateProps = {
  children: ReactNode;
};

const AuthGate = ({ children }: AuthGateProps) => {
  const { t } = useTranslate();
  const oidc = useOidc();
  const isCallbackRoute = window.location.pathname === CALLBACK_PATH;
  const [sessionReady, setSessionReady] = useState(isCallbackRoute);
  const { value: hasAllowedRole, isLoading: isRoleLoading } = useIsAdminOrUser();

  const isOidcLoggedIn = oidc.isUserLoggedIn;
  const oidcLogin = oidc.isUserLoggedIn ? undefined : oidc.login;

  useEffect(() => {
    if (isCallbackRoute) return;

    // Une session Keycloak expirée ou absente coupe l'accès : on ferme aussi la session Directus.
    if (!isOidcLoggedIn) {
      void (async () => {
        await logoutDirectusSession();
        baseApi.util.resetApiState();
        await oidcLogin?.({
          redirectUrl: window.location.pathname + window.location.search,
        });
      })();
      return;
    }

    initDirectusSession().then(ok => {
      if (ok) {
        setSessionReady(true);
        return;
      }

      sessionStorage.setItem(
        'directus_redirect_after',
        window.location.pathname + window.location.search,
      );
      const callbackUrl = window.location.origin + CALLBACK_PATH;
      window.location.href = `${import.meta.env.VITE_DIRECTUS_URL}/auth/login/${DIRECTUS_SSO_PROVIDER}?redirect=${encodeURIComponent(callbackUrl)}`;
    });
  }, [isCallbackRoute, isOidcLoggedIn, oidcLogin]);

  if (!sessionReady || (!isCallbackRoute && !isOidcLoggedIn)) return <OidcLoadingFallback />;
  if (isCallbackRoute) return <>{children}</>;

  if (isRoleLoading) {
    return (
      <Center mih="100vh" px="md">
        <Stack align="center" gap="md">
          <Loader />
          <Text c="dimmed">{t('auth.role.loading')}</Text>
        </Stack>
      </Center>
    );
  }

  if (hasAllowedRole || debug) {
    return <>{children}</>;
  }

  const handleReconnect = async () => {
    await logoutDirectusSession();
    await oidc.login?.({ redirectUrl: loginRedirectUrl });
  };

  return (
    <Center mih="100vh" p="md">
      <Stack align="center" gap="md" maw={560} ta="center">
        <Text>{t('roleGate.unavailable')}</Text>
        <Button leftSection={<IconRefresh size={16} />} onClick={() => void handleReconnect()}>
          {t('roleGate.reconnect')}
        </Button>
      </Stack>
    </Center>
  );
};

export default AuthGate;
